import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { removeEmptyValues } from '@nike/ux-tread-dotcom-utils';
import { LOCATION_TYPES, FULFILLMENT_TYPES } from '../constants';
import determineFulfillmentType from './determine-fulfillment-type';
import addLocationAttributes from './add-location-attributes';
/**
 * @memberof ux-tread-fulfillment-offerings
 * @description An abstraction for handling async CQRS endpoints aka jobs
 * @param {object} parameters - named parameters
 * @property {string} country Capitalized country for request
 * @property {Array.FulfillmentOfferingItem} items - incoming items to be mapped over and normalized
 * @property {FulfillmentOfferingItemLocation} location - location information to be used for mapping over items
 * @property {string} unitOfMeasure - enums for distance calculated in miles (mi) or kilometers (km)
 * @property {boolean} [isSearch = false] - whether or not to search an area for stores for pickup
 * @property {Array.FulfillmentOfferingSearchFilter} - filter search request by type and count ex. [{ locationType: LOCATION_TYPES.pickupPoints, count: 0 }]
 * @returns {Array.FulfillmentOfferingItem} an array of fulfillmentOffering items meant for PUT request
 */

export default function normalizeItemsForFulfillmentOfferings(_ref) {
  var country = _ref.country,
      isSearch = _ref.isSearch,
      items = _ref.items,
      location = _ref.location,
      distance = _ref.distance,
      unitOfMeasure = _ref.unitOfMeasure,
      _ref$locationTypes = _ref.locationTypes,
      locationTypes = _ref$locationTypes === void 0 ? LOCATION_TYPES : _ref$locationTypes,
      _ref$filter = _ref.filter,
      filter = _ref$filter === void 0 ? [] : _ref$filter;
  var postalCode = location.postalCode,
      latitude = location.latitude,
      longitude = location.longitude,
      postalAddress = location.postalAddress,
      locationId = location.id;
  return items.map(function (item) {
    var _item$shippingAddress;

    // without having user intent we have to make some determination about the request and this is the current thought
    var determinedType = determineFulfillmentType({
      item: item
    });
    var email = (_item$shippingAddress = item.shippingAddress) === null || _item$shippingAddress === void 0 ? void 0 : _item$shippingAddress.email;
    var baseSearchValues = {
      type: locationTypes.search,
      radius: {
        distance: distance,
        unitOfMeasure: unitOfMeasure
      },
      filter: filter
    };
    var fulfillmentType = item.fulfillmentType,
        giftCard = item.giftCard,
        id = item.id,
        promotionCodes = item.promotionCodes,
        quantity = item.quantity,
        skuId = item.skuId,
        valueAddedServices = item.valueAddedServices;
    var mappedValueAddedServices = valueAddedServices !== null && valueAddedServices !== void 0 && valueAddedServices.length ? valueAddedServices.map(function (vas) {
      var _ref2 = (vas === null || vas === void 0 ? void 0 : vas.instruction) || {},
          designView = _ref2.designView,
          instruction = _objectWithoutProperties(_ref2, ["designView"]);

      return {
        id: vas.id,
        instruction: instruction
      };
    }) : [];
    var normalizedItem = removeEmptyValues({
      fulfillmentType: fulfillmentType,
      giftCard: giftCard,
      id: id,
      promotionCodes: promotionCodes,
      quantity: quantity,
      skuId: skuId,
      valueAddedServices: mappedValueAddedServices
    });

    switch (determinedType) {
      case FULFILLMENT_TYPES.digital:
        {
          return _objectSpread(_objectSpread({}, normalizedItem), {}, {
            locations: [{
              type: locationTypes.digital,
              digitalAddress: {
                country: country,
                email: email
              }
            }]
          });
        }

      case FULFILLMENT_TYPES.ship:
        {
          return _objectSpread(_objectSpread({}, normalizedItem), {}, {
            locations: [removeEmptyValues({
              type: locationTypes.shipping,
              postalAddress: postalAddress
            }, {
              removeEmptyStrings: true
            })]
          });
        }

      case FULFILLMENT_TYPES.pickup:
        {
          var locations;

          if (isSearch) {
            locations = [_objectSpread(_objectSpread({}, baseSearchValues), {}, {
              latitude: latitude,
              longitude: longitude
            }), _objectSpread(_objectSpread({}, baseSearchValues), {}, {
              postalCode: postalCode
            })].map(addLocationAttributes).filter(Boolean);
          } else {
            locations = [{
              type: locationTypes.pickupPoints,
              id: locationId
            }];
          }

          return _objectSpread(_objectSpread({}, normalizedItem), {}, {
            locations: locations
          });
        }

      default:
        {
          var _locations = [removeEmptyValues({
            type: locationTypes.shipping,
            postalAddress: postalAddress
          }, {
            removeEmptyStrings: true
          }), _objectSpread(_objectSpread({}, baseSearchValues), {}, {
            latitude: latitude,
            longitude: longitude
          }), _objectSpread(_objectSpread({}, baseSearchValues), {}, {
            postalCode: postalCode
          })].map(addLocationAttributes).filter(Boolean);

          if (locationId) {
            _locations.push({
              type: locationTypes.pickupPoints,
              id: locationId
            });
          }

          return _objectSpread(_objectSpread({}, normalizedItem), {}, {
            locations: _locations
          });
        }
    }
  });
}